import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { findKey, inRange } from 'lodash';
import { ButtonGroup, Col, Dropdown, DropdownButton, Form, InputGroup, Row } from 'react-bootstrap';
import Search from 'mdi-react/SearchIcon';
import HighlightOff from 'mdi-react/HighlightOffIcon';

import { RootState } from 'rootReducer';
import { defaultGridState } from 'modules/common/constants';
import { MIN_FILTER_VALUE_LENGTH } from 'modules/security/containers/TripsTabContainer/constants';
import { LogicTypes, FilterOperators, DataGrid, DataGridColumn } from 'modules/shared/DataGrid';
import { Filter, GridFilter } from 'modules/shared/DataGrid/utils/interfaces';
import SmallSpinner from 'modules/shared/SmallSpinner';
import { useGridState } from 'modules/shared/DataGrid/hooks/useGridState';
import { SecurityReportProps } from '../interfaces';
import { get2359ReportsRequest, set2359ReportsFilter } from './actions';
import { fieldsNames } from './constants';
import classes from '../ReportLoading/ReportLoading.module.scss';
import ReportBookingDate from '../cell/ReportBokingDate';

const timeDelay = {
  0: '1 година',
  1: '2 години',
  2: 'Більше 2-х годин',
};

const timeDelayPeriod = {
  0: [0, 60],
  1: [61, 120],
  2: [120, 9007199254740991],
} as {
  [key: string]: any;
};

const Report2359: React.FC<SecurityReportProps> = React.memo(({ onRow }) => {
  const dispatch = useDispatch();
  const [filterInputValue, setFilterInputValue] = useState('');
  const [tripDelay, setTripDelay] = useState('');
  const filterInputRef = useRef<HTMLInputElement>(null);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterInputValue(e.target.value);
  };

  const branchLocal = localStorage.getItem('branchId') || 'null';
  const branchId = JSON.parse(branchLocal)?.id;

  const { reports, allReports, loading } = useSelector(
    (state: RootState) => state.securityModuleReducer.reportsReducer.report2359Reducer,
  );

  const gridState = useGridState(reports, defaultGridState);

  const handleClearIconClick = () => {
    setFilterInputValue('');
    if (filterInputRef.current) {
      filterInputRef.current.focus();
    }
  };

  useEffect(() => {
    if (branchId) {
      dispatch(get2359ReportsRequest(`${branchId}`));
    }
  }, []);

  useEffect(() => {
    if (filterInputValue.length < MIN_FILTER_VALUE_LENGTH) {
      gridState.setFilter({
        logic: LogicTypes.and,
        filters: [],
      });
    } else {
      const newFilter = {
        logic: LogicTypes.or,
        filters: fieldsNames.map(
          (name): Filter => ({
            operator: FilterOperators.contains,
            value: filterInputValue,
            field: name,
          }),
        ),
      } as GridFilter;
      gridState.setFilter(newFilter);
    }
  }, [filterInputValue]);

  useEffect(() => {
    // eslint-disable-next-line
    if (!!tripDelay) {
      const delayIndex: string = findKey(timeDelay, (value: string) => value === tripDelay) || '';

      if (delayIndex) {
        const period = timeDelayPeriod[delayIndex];
        const filter = allReports.filter((rep: any) => inRange(rep.delayInStation, period[0], period[1]));

        dispatch(set2359ReportsFilter(filter));
      }
    }
  }, [tripDelay]);

  return (
    <React.Fragment>
      <Row className="pb-3 mx-0 px-0">
        <div>
          <span>Час затримки на СМЦ:</span>
          <DropdownButton
            variant="outline-secondary btn-reports"
            title={tripDelay || 'Всі'}
            as={ButtonGroup}
            className="ml-2"
          >
            {Object.entries(timeDelay).map(([key, value]: [string, string]) => (
              <Dropdown.Item key={key} eventKey={value} onSelect={(e: any) => setTripDelay(e)}>
                {value}
              </Dropdown.Item>
            ))}
          </DropdownButton>
        </div>
        <Col className="d-flex align-items-center pb-3 pr-0 justify-content-end">
          <InputGroup className={classes.filter}>
            <Form.Control
              as="input"
              type="text"
              name="filter"
              onChange={handleFilterChange}
              value={filterInputValue}
              ref={filterInputRef}
            />
            <Search size={18} className={classes.searchIcon} />
            {!!filterInputValue && (
              <HighlightOff size={18} className={classes.clearFilterIcon} onClick={handleClearIconClick} />
            )}
          </InputGroup>
        </Col>
      </Row>
      {loading ? (
        <SmallSpinner />
      ) : (
        <DataGrid
          pageable
          data={gridState.gridData}
          sort={gridState.sort}
          total={gridState.total}
          skip={gridState.pagination?.skip}
          pageSize={gridState.pagination?.pageSize}
          onPageChange={gridState.handlePageChange}
          onSortChange={gridState.handleSortChange}
          onRow={onRow}
        >
          <DataGridColumn title="Номер рейса" dataIndex="tripId" key="tripId" width="60px" sortable />
          <DataGridColumn
            title="Дата бронювання"
            dataIndex="bookingDate"
            key="bookingDate"
            render={ReportBookingDate}
            width="10%"
            className={classes.bookingDate}
          />
          <DataGridColumn title="Затримка на СМЦ, хв" dataIndex="delayInStation" key="delayInStation" width="5%" />
          <DataGridColumn sortable title="Статус рейса" dataIndex="tripStatus" key="tripStatus" width="5%" />
          <DataGridColumn
            sortable
            title="Дата і час отримання статусу"
            dataIndex="currentStatusSetDt"
            key="currentStatusSetDt"
            width="5%"
          />
          <DataGridColumn title="Держномер ТЗ" dataIndex="vehicleLicensePlate" key="vehicleLicensePlate" width="5%" />
          <DataGridColumn
            title="Держномер причепа"
            dataIndex="trailerLicensePlate"
            key="trailerLicensePlate"
            width="5%"
          />
          <DataGridColumn title="Водій" dataIndex="driver" key="driver" width="5%" />
          <DataGridColumn title="Телефон" dataIndex="driverPhone" key="driverPhone" width="5%" />
          <DataGridColumn title="Водійське посвідчення" dataIndex="driverDocument" key="driverDocument" width="5%" />
          <DataGridColumn title="Експедитор" dataIndex="forwarder" key="forwarder" width="5%" />
          <DataGridColumn title="Телефон" dataIndex="forwarderPhone" key="forwarderPhone" width="5%" />
          <DataGridColumn title="Номер документа" dataIndex="forwarderDocument" key="forwarderDocument" width="5%" />
          <DataGridColumn title="Дата і час в'їзду" dataIndex="arrivalDate" key="arrivalDate" width="5%" />
          <DataGridColumn title="Коментар" dataIndex="entrySecurityMessage" key="entrySecurityMessage" width="5%" />
        </DataGrid>
      )}
    </React.Fragment>
  );
});

export default Report2359;
